import { formatCurrency, formatDate } from './formatters';

/**
 * Generates and triggers a print job for a quotation in an isolated window.
 * This ensures zero interference from the main application's CSS or state.
 * 
 * @param {Object} formData - Main order data
 * @param {Array} items - Order items
 * @param {Object} client - Client data
 * @param {Array} products - List of all products (for name fallback)
 */
export const printQuotation = (formData, items, client, products = []) => {
    console.log('[PrintUtility] Generating isolated print payload for Order #', formData.ID);

    const win = window.open('', '_blank');
    if (!win) {
        alert("O bloqueador de pop-ups impediu a abertura da tela de impressão. Por favor, autorize pop-ups para este aplicativo.");
        return;
    }

    const budgetId = formData.ID || '--';
    const clientName = client?.SobrenomeContato || 'N/A';
    const budgetDate = formatDate(formData.DataPedido) || '--/--/----';
    const longDate = formatDate(new Date());

    // Pre-calculate totals (mirroring OrderModal.js logic)
    const totalMaterial = items.reduce((sum, item) => sum + (Number(item.Quantidade) * Number(item.PrecoUnitario)), 0);
    const materialEFuncionario = totalMaterial + Number(formData.VLEmpreiteira);
    const impostoServico = Number(formData.Frete) * (Number(formData.AliquotaImposto) / 100);
    const lucroSemImposto = Number(formData.Frete) - materialEFuncionario;
    const lucroComImposto = lucroSemImposto - impostoServico;
    const valorEntrada = Number(formData.Frete) * (Number(formData.Entrada) / 100);
    const saldoRestante = Number(formData.Frete) - valorEntrada;
    const valorParcela = formData.Quant > 0 ? saldoRestante / formData.Quant : 0;

    const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Orçamento #${budgetId} - ${clientName}</title>
            <style>
                @page { margin: 1cm; size: A4; }
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                    padding: 20px; 
                    color: #000; 
                    background: #fff; 
                    margin: 0;
                    line-height: 1.3;
                }
                
                /* Layout Styles (Isolated) */
                .print-legacy-info-box { border: 2px solid #000; padding: 10px; margin-bottom: 5px; background: #fff; }
                .print-info-row { display: flex; justify-content: space-between; margin-bottom: 5px; width: 100%; }
                .print-info-col { width: 48%; display: flex; gap: 8px; }
                .print-info-col.right { text-align: right; justify-content: flex-end; }
                
                .legacy-label { color: #800000; font-weight: bold; font-size: 11pt; }
                .legacy-value { font-size: 11pt; color: #000; }
                
                .print-legacy-obs-box { border: 2px solid #000; border-top: none; padding: 10px; min-height: 50px; display: flex; gap: 10px; background: #fff; }
                .legacy-obs-content { font-size: 11pt; flex: 1; white-space: pre-wrap; color: #000; }
                
                .print-legacy-items-section { margin-top: 15px; }
                .print-legacy-table { width: 100%; border-collapse: collapse; border: 2px solid #000; table-layout: fixed; background: #fff; }
                .print-legacy-table th, .print-legacy-table td { border: 1px solid #000; padding: 6px; font-size: 10pt; word-wrap: break-word; }
                .legacy-header-bg th { color: #800000; text-align: left; background: #eee; font-weight: bold; }
                
                .print-legacy-totals-grid { margin-top: 15px; }
                .legacy-totals-top, .legacy-totals-bottom { display: flex; border: 2px solid #000; padding: 10px; justify-content: space-between; background: #fff; }
                .legacy-totals-bottom { border-top: none; }
                .legacy-left-col, .legacy-right-col { width: 48%; display: flex; flex-direction: column; gap: 6px; }
                
                .total-item { display: flex; justify-content: space-between; width: 100%; }
                .total-item.inline { justify-content: flex-start; gap: 15px; }
                .legacy-value-inline { font-weight: bold; min-width: 40px; }
                .legacy-date-row { margin-top: 15px; font-style: italic; font-size: 10pt; text-align: left; color: #333; }

                @media print {
                    body { padding: 0; }
                }
            </style>
        </head>
        <body>
            <div class="print-legacy-info-box">
                <div class="print-info-row">
                    <div class="print-info-col"><span class="legacy-label">Cliente:</span><span class="legacy-value">${clientName}</span></div>
                    <div class="print-info-col right"><span class="legacy-label">Orçamento Nº:</span><span class="legacy-value">${budgetId}</span></div>
                </div>
                <div class="print-info-row">
                    <div class="print-info-col"><span class="legacy-label">Contato:</span><span class="legacy-value">${client?.PrimeiroNomeContato || '---'}</span></div>
                    <div class="print-info-col right"><span class="legacy-label">Data:</span><span class="legacy-value">${budgetDate}</span></div>
                </div>
                <div class="print-info-row">
                    <div class="print-info-col"><span class="legacy-label">Endereço:</span><span class="legacy-value">${client?.EnderecoCobranca || ''}</span></div>
                    <div class="print-info-col right"><span class="legacy-label">Cidade:</span><span class="legacy-value">${client?.Cidade || ''}, ${client?.EstadoProvincia || ''}</span></div>
                </div>
                <div class="print-info-row">
                    <div class="print-info-col"><span class="legacy-label">Tel:</span><span class="legacy-value">${client?.Telefone || ''}</span></div>
                </div>
            </div>

            <div class="print-legacy-obs-box">
                <span class="legacy-label">OBS:</span>
                <div class="legacy-obs-content">${formData.OBS || ""}</div>
            </div>

            <div class="print-legacy-items-section">
                <table class="print-legacy-table">
                    <thead>
                        <tr class="legacy-header-bg">
                            <th style="width: 10%; text-align: center;">Cod</th>
                            <th style="width: 50%">Descrição do Produto</th>
                            <th style="width: 10%; text-align: center;">Qtd</th>
                            <th style="width: 15%; text-align: right;">Unitário</th>
                            <th style="width: 15%; text-align: right;">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${items.map(item => `
                            <tr>
                                <td style="text-align: center">${item.IDProduto || '--'}</td>
                                <td>${item.NomeProduto || (Array.isArray(products) ? products.find(p => String(p.ID) === String(item.IDProduto))?.NomeProduto : '') || '---'}</td>
                                <td style="text-align: center">${item.Quantidade}</td>
                                <td style="text-align: right">${formatCurrency(item.PrecoUnitario)}</td>
                                <td style="text-align: right">${formatCurrency(Number(item.Quantidade) * Number(item.PrecoUnitario))}</td>
                            </tr>
                        `).join('')}
                        ${Array(Math.max(0, 10 - items.length)).fill(0).map(() => `
                            <tr style="height: 24px"><td>&nbsp;</td><td></td><td></td><td></td><td></td></tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>

            <div class="print-legacy-totals-grid">
                <div class="legacy-totals-top">
                    <div class="legacy-left-col">
                        <div class="total-item"><span class="legacy-label">Valor do Orçamento</span><span class="legacy-value">${formatCurrency(formData.Frete)}</span></div>
                        <div class="total-item"><span class="legacy-label">Lucro s/ Imposto</span><span class="legacy-value">${formatCurrency(lucroSemImposto)}</span></div>
                        <div class="total-item"><span class="legacy-label">Lucro c/ Imposto</span><span class="legacy-value">${formatCurrency(lucroComImposto)}</span></div>
                    </div>
                    <div class="legacy-right-col">
                        <div class="total-item"><span class="legacy-label">Material Total</span><span class="legacy-value">${formatCurrency(totalMaterial)}</span></div>
                        <div class="total-item"><span class="legacy-label">Mão de Obra</span><span class="legacy-value">${formatCurrency(formData.VLEmpreiteira)}</span></div>
                        <div class="total-item"><span class="legacy-label">Subtotal Mat+MO</span><span class="legacy-value">${formatCurrency(materialEFuncionario)}</span></div>
                    </div>
                </div>
                <div class="legacy-totals-bottom">
                    <div class="legacy-left-col">
                        <div class="total-item inline">
                            <span class="legacy-label">ISS (${(Number(formData.AliquotaImposto) || 0).toFixed(0)}%)</span>
                            <span class="legacy-value">${formatCurrency(impostoServico)}</span>
                        </div>
                        <div class="legacy-date-row">Gerado em: ${longDate}</div>
                    </div>
                    <div class="legacy-right-col">
                        <div class="total-item inline"><span class="legacy-label">Entrada (${(Number(formData.Entrada) || 0).toFixed(0)}%)</span><span class="legacy-value">${formatCurrency(valorEntrada)}</span></div>
                        <div class="total-item"><span class="legacy-label">Saldo a Pagar</span><span class="legacy-value">${formatCurrency(saldoRestante)}</span></div>
                        <div class="total-item inline"><span class="legacy-label">Parcelamento</span><span class="legacy-value-inline">${formData.Quant}x</span><span class="legacy-value">${formatCurrency(valorParcela)}</span></div>
                    </div>
                </div>
            </div>

            <script>
                window.onload = function() {
                    console.log('[PrintWindow] Initializing print...');
                    window.print();
                    setTimeout(() => {
                        window.close();
                    }, 500);
                };
            </script>
        </body>
        </html>
    `;

    win.document.write(printContent);
    win.document.close();
};
